import {
  Button, Checkbox, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader,
} from '@heroui/react';
import {
  FileIcon, Folder, LoaderCircle,
} from 'lucide-react';
import React, {
  FC, memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useOrganizationState } from '@/state';
import { getYearMonthPath } from '@/utils';
import { getBytescaleImageUrl } from '@/utils/getImageUrl';
import { useMediaLibrary } from '@/views/MediaLibrary/queries/useMediaLibrary';
import { MediaFile } from '@/views/MediaLibrary/types';

// Klasyczny czarny przycisk — spójny z komponentem AddButton.
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif|svg|bmp|ico)$/i;
const isImage = (name: string): boolean => IMAGE_EXT.test(name);
const isPdf = (name: string): boolean => /\.pdf$/i.test(name);
const thumb = (url: string): string => getBytescaleImageUrl(url, 'BYTESCALE', { query: 'w=300&h=300&fit=crop&f=webp' });

// Który plik pokazać wg mimeTypes uploadera. Bez `accept` → obrazy (domyślne uploadery obrazów).
const fileAllowed = (name: string, accept?: string[]): boolean => {
  if (!accept || !accept.length) return isImage(name);
  const allowImages = accept.some((m) => m.startsWith('image/'));
  const allowPdf = accept.includes('application/pdf');
  if (allowImages && isImage(name)) return true;
  if (allowPdf && isPdf(name)) return true;
  return false;
};

// Ile plików pokazujemy na raz (reszta pod „Pokaż więcej") — bez tego siatka renderuje
// setki <img> naraz i klik zaznaczenia przemula całą listę.
const PAGE_SIZE = 48;

interface PickerTileProps {
  file: MediaFile;
  isSelected: boolean;
  onToggle: (url: string) => void;
}

// Memo: klik zaznaczenia re-renderuje TYLKO ten kafelek, nie całą siatkę.
const PickerTile = memo(({ file, isSelected, onToggle }: PickerTileProps) => {
  const img = isImage(file.name);
  return (
    <button
      type="button"
      onClick={() => onToggle(file.url)}
      className={`group relative rounded-lg overflow-hidden border bg-white dark:bg-content2 transition-all text-left ${isSelected ? 'border-primary ring-2 ring-primary' : 'border-default-200 hover:border-primary'}`}
    >
      <div className="block w-full aspect-square bg-white dark:bg-content2">
        {img ? (
          <img src={thumb(file.url)} alt={file.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-default-400"><FileIcon size={30} /></div>
        )}
      </div>
      <div className={`absolute top-1.5 left-1.5 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <Checkbox
          size="sm"
          isSelected={isSelected}
          classNames={{ base: 'm-0 p-1 rounded-md bg-black/45 backdrop-blur-sm pointer-events-none' }}
          aria-label="select"
        />
      </div>
      <div className="px-2 py-1.5 border-t border-default-100">
        <p className="text-[11px] text-foreground truncate" title={file.name}>{file.name}</p>
      </div>
    </button>
  );
});
PickerTile.displayName = 'PickerTile';

interface MediaLibraryPickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Wielokrotny wybór (galeria). Domyślnie false = jeden plik. */
  multiple?: boolean;
  /** mimeTypes uploadera — filtruje pokazywane pliki (obrazy vs PDF). Brak → obrazy. */
  accept?: string[];
  /** Zwraca wybrane URL-e po kliknięciu „Wybierz". */
  onSelect: (urls: string[]) => void;
  /**
   * Klasa z-indexu (np. `z-[1260]`). Potrzebna, gdy picker otwiera się Z WNĘTRZA innego
   * modala: domyślna warstwa HeroUI ląduje wtedy POD nim i biblioteka jest widoczna,
   * ale nieklikalna. Domyślnie bez nadpisania.
   */
  zIndexClass?: string;
}

/**
 * Picker istniejących plików z Biblioteki mediów — do wstawiania w formularze zamiast
 * uploadu. Nawigacja rok→miesiąc, start na bieżącym miesiącu, multi-select, „Wybierz"
 * zwraca URL-e. Nie kadruje. Paginacja + memo kafelków dla płynności przy setkach plików.
 */
export const MediaLibraryPicker: FC<MediaLibraryPickerProps> = ({
  isOpen, onClose, multiple = false, accept, onSelect, zIndexClass,
}) => {
  const { t } = useTranslation();
  const { organizationId } = useOrganizationState();
  const [path, setPath] = useState<string>(getYearMonthPath());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Query odpala się tylko gdy modal otwarty (bez tła pobierania, gdy zamknięty).
  const { data, isLoading, isError, refetch } = useMediaLibrary(organizationId, isOpen ? path : '');

  // Reset przy otwarciu: bieżący miesiąc, brak zaznaczenia, pierwsza strona.
  useEffect(() => {
    if (isOpen) {
      setPath(getYearMonthPath());
      setSelected(new Set());
      setVisibleCount(PAGE_SIZE);
    }
  }, [isOpen]);

  // Zmiana folderu → od nowa paginacja.
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [path]);

  const folders = data?.folders ?? [];
  const files = useMemo(() => (data?.files ?? []).filter((f) => fileAllowed(f.name, accept)), [data, accept]);
  const visibleFiles = useMemo(() => files.slice(0, visibleCount), [files, visibleCount]);
  const hasMore = visibleCount < files.length;
  const segments = useMemo(() => (path ? path.split('/') : []), [path]);
  const isEmpty = !isLoading && !isError && folders.length === 0 && files.length === 0;

  const toggle = useCallback((url: string): void => {
    setSelected((prev) => {
      if (!multiple) return prev.has(url) ? new Set() : new Set([url]);
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  }, [multiple]);

  const navigate = (p: string): void => { setPath(p); };

  const handleConfirm = (): void => {
    if (!selected.size) return;
    onSelect(Array.from(selected));
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      backdrop="opaque"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-white dark:bg-[#1a1f26] rounded-md shadow-2xl border border-gray-200 dark:border-[#374151]',
        closeButton: 'top-3 right-3 z-20',
        ...(zIndexClass ? { wrapper: zIndexClass, backdrop: `${zIndexClass} bg-black/50 backdrop-blur-sm` } : { backdrop: 'bg-black/50 backdrop-blur-sm' }),
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-3 px-6 pt-6 pb-2">
          <span className="text-[20px] font-semibold leading-tight text-text-primary">
            {t('mediaLibrary.pickerTitle', 'Wybierz z biblioteki mediów')}
          </span>
          <div className="flex items-center gap-1 flex-wrap text-[14px] font-normal -ml-2">
            <button
              type="button"
              onClick={() => navigate('')}
              className={`px-2 py-1 rounded-md transition-colors hover:bg-default-100 ${segments.length === 0 ? 'font-medium text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}
            >
              {t('mediaLibrary.rootLabel', 'Media')}
            </button>
            {segments.map((seg, idx) => {
              const segPath = segments.slice(0, idx + 1).join('/');
              const isLast = idx === segments.length - 1;
              return (
                <React.Fragment key={segPath}>
                  <span className="text-default-300">/</span>
                  <button
                    type="button"
                    onClick={() => navigate(segPath)}
                    className={`px-2 py-1 rounded-md transition-colors hover:bg-default-100 ${isLast ? 'font-medium text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}
                  >
                    {seg}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </ModalHeader>

        <ModalBody className="min-h-[320px] px-6">
          {isLoading && (
            <div className="flex items-center gap-2 py-2 text-[15px] text-text-secondary">
              <LoaderCircle size={16} className="animate-spin" aria-hidden />
              {t('common.loading', 'Ładowanie')}
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center gap-3 py-20 text-center">
              <p className="text-default-500">{t('mediaLibrary.listError', 'Nie udało się wczytać biblioteki mediów.')}</p>
              <Button size="sm" variant="flat" onPress={() => refetch()}>{t('common.retry', 'Spróbuj ponownie')}</Button>
            </div>
          )}

          {isEmpty && (
            // Pusty stan jak w tabelach: sam tekst, w lewym górnym rogu.
            <div className="flex flex-col gap-1 py-2">
              <p className="m-0 text-[15px] font-medium text-text-primary">{t('mediaLibrary.pickerEmpty', 'Brak plików w tym folderze')}</p>
              <p className="m-0 text-[14px] text-text-secondary">{t('mediaLibrary.pickerEmptyHint', 'Zmień miesiąc/rok powyżej albo prześlij nowe pliki.')}</p>
            </div>
          )}

          {!isLoading && !isError && !isEmpty && (
            <div className="flex flex-col gap-6">
              {folders.length > 0 && (
                <section>
                  <h3 className="text-xs font-light uppercase tracking-wide text-default-400 mb-3">
                    {t('mediaLibrary.folders', 'Foldery')}
                  </h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
                    {folders.map((f) => (
                      <button
                        key={f.path}
                        type="button"
                        onClick={() => navigate(f.path)}
                        className="group flex flex-col items-center gap-1.5 p-3 rounded-lg border border-default-200 bg-white dark:bg-content2 hover:border-primary hover:shadow-sm transition-all"
                      >
                        <Folder size={28} className="text-primary/80 group-hover:text-primary transition-colors" fill="currentColor" fillOpacity={0.12} />
                        <span className="text-xs font-medium text-foreground truncate max-w-full">{f.name}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {files.length > 0 && (
                <section>
                  <h3 className="text-xs font-light uppercase tracking-wide text-default-400 mb-3">
                    {t('mediaLibrary.files', 'Pliki')}
                    {' · '}
                    {hasMore ? `${visibleFiles.length}/${files.length}` : files.length}
                  </h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
                    {visibleFiles.map((file) => (
                      <PickerTile
                        key={file.path}
                        file={file}
                        isSelected={selected.has(file.url)}
                        onToggle={toggle}
                      />
                    ))}
                  </div>

                  {hasMore && (
                    <div className="flex justify-center mt-5">
                      <Button size="sm" variant="flat" onPress={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                        {t('mediaLibrary.loadMore', 'Pokaż więcej')}
                      </Button>
                    </div>
                  )}
                </section>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter className="px-6 pt-4 pb-6 gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-11 px-5 rounded-full border border-[#d1d5db] bg-white text-[14px] font-medium text-text-primary hover:bg-bg-muted"
          >
            {t('common.cancel', 'Anuluj')}
          </button>
          <button
            type="button"
            disabled={selected.size === 0}
            onClick={handleConfirm}
            className="h-11 px-6 rounded-full bg-accent-ink text-white text-[14px] font-medium hover:bg-black
              disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {selected.size > 0
              ? t('mediaLibrary.pickerSelectCount', 'Wybierz ({{count}})', { count: selected.size })
              : t('mediaLibrary.pickerSelect', 'Wybierz')}
          </button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
